import profileAvatar from 'assets/avatars/profile-avatar.jpg';
import profileBanner from 'assets/avatars/profile-banner.svg';

import { PersonaId } from '../personas';
import { PERSONA_MOCKS, PersonaMock } from './persona-mocks';

const SAMPLE_CATEGORIES = ['Quality', 'Communication', 'Punctuality', 'Value'];
const RESPONSE_TIME_HOURS = 2;
const JOBS_PER_REVIEW_MULTIPLIER = 1.6;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_MONTH = 30 * MS_PER_DAY;
const MS_PER_YEAR = 365.25 * MS_PER_DAY;
const DEFAULT_JOINED_MONTHS_AGO = 6;
const REVIEW_DAYS_AGO = [3, 7, 14];

const REVIEWER_LOCATIONS = ['Austin, TX', 'San Diego, CA', 'Brooklyn, NY'];

// Fixed reference so synthesized timestamps are deterministic across renders
// (avoids `Date.now()` and the React/SSR drift it creates).
const REFERENCE_TIMESTAMP = Date.parse('2026-06-01T00:00:00Z');

const PRICE_NUMBER_REGEX = /\d[\d,]*(?:\.\d+)?/g;

const splitName = (full: string): { first: string; last: string } => {
  const parts = full.trim().split(/\s+/);
  return {
    first: parts[0] ?? 'Demo',
    last: parts.slice(1).join(' '),
  };
};

const parseJoinedTimestamp = (joinedLine: string): string => {
  const yearMatch = joinedLine.match(/(\d+)\s*year/i);
  if (yearMatch) {
    return new Date(
      REFERENCE_TIMESTAMP - Number(yearMatch[1]) * MS_PER_YEAR,
    ).toISOString();
  }
  const monthMatch = joinedLine.match(/(\d+)\s*month/i);
  if (monthMatch) {
    return new Date(
      REFERENCE_TIMESTAMP - Number(monthMatch[1]) * MS_PER_MONTH,
    ).toISOString();
  }
  return new Date(
    REFERENCE_TIMESTAMP - DEFAULT_JOINED_MONTHS_AGO * MS_PER_MONTH,
  ).toISOString();
};

const parsePriceRange = (
  priceStr: string,
): { min: number | null; max: number | null } => {
  const matches = priceStr.match(PRICE_NUMBER_REGEX) ?? [];
  const nums = matches
    .map((match) => Number(match.replace(/,/g, '')))
    .filter((number) => Number.isFinite(number));
  if (nums.length === 0) return { min: null, max: null };
  if (nums.length === 1) return { min: nums[0], max: nums[0] };
  return { min: Math.min(...nums), max: Math.max(...nums) };
};

const synthesizeServices = (mock: PersonaMock): Kudable.Service[] =>
  mock.services.map((service, index) => {
    const { min, max } = parsePriceRange(service.price);
    return {
      id: `demo-svc-${index}`,
      name: service.title,
      price_min: min,
      price_max: max,
      price_note: service.priceNote ?? null,
      engagement_type: 'appointment',
    };
  });

const synthesizeReviews = (mock: PersonaMock): Kudable.Review[] =>
  mock.reviews.map((review, index) => {
    const daysAgo = REVIEW_DAYS_AGO[index] ?? 30;
    return {
      id: `demo-rev-${index}`,
      reviewer_name: review.author,
      overall_rating: review.rating,
      comment: review.snippet,
      verified: true,
      job_confirmed: true,
      badge: null,
      improvement_suggestion: null,
      service_name: null,
      days_after_job: null,
      original_overall_rating: null,
      original_comment: null,
      was_edited: false,
      updated_at: null,
      inserted_at: new Date(
        REFERENCE_TIMESTAMP - daysAgo * MS_PER_DAY,
      ).toISOString(),
      ratings: SAMPLE_CATEGORIES.map((category) => ({
        category,
        rating: review.rating,
      })),
      assets: [],
      contactable: false,
      has_responded: false,
      reviewer_organisation:
        REVIEWER_LOCATIONS[index % REVIEWER_LOCATIONS.length],
    };
  });

const synthesizeProvider = (
  personaId: PersonaId,
  mock: PersonaMock,
): Kudable.Provider => {
  const { first, last } = splitName(mock.displayName);
  return {
    id: `demo-${personaId}`,
    first_name: first,
    last_name: last,
    title: mock.professionLine,
    profile_token: `demo-${personaId}`,
    avatar_url: profileAvatar,
    workplace: null,
    business_name: null,
    company_mode: false,
    accepts_bookings: true,
    accepts_projects: true,
    currency: 'USD',
    inserted_at: parseJoinedTimestamp(mock.joinedLine),
    team_members: [],
    provider_mode: 'services',
    social_links: {
      instagram: '#',
      twitter: '#',
    },
    promotes_categories: [],
    promotes_niche: null,
    open_to_collabs: false,
    tagline: null,
    theme_preset: 'personal',
    accent_color: null,
    avatar_border_color: null,
    banner_url: profileBanner,
    logo_url: null,
    featured_team_members: [],
    pitch_questions: [],
  };
};

const synthesizeReputation = (mock: PersonaMock): Kudable.Reputation => ({
  score: mock.rating,
  review_count: mock.reviewCount,
  verified_count: mock.reviewCount,
  invited_count: 0,
  jobs_completed: Math.round(mock.reviewCount * JOBS_PER_REVIEW_MULTIPLIER),
  category_averages: SAMPLE_CATEGORIES.map((category) => ({
    category,
    average: mock.rating,
  })),
});

export type SynthesizedProfile = {
  provider: Kudable.Provider;
  reputation: Kudable.Reputation;
  services: Kudable.Service[];
  reviews: Kudable.Review[];
  responseTimeHours: number | null;
  intents: Profile.IntentKey[];
  subscriptionActive: boolean;
};

export const synthesizeProfile = (
  personaId: PersonaId,
): SynthesizedProfile => {
  const mock = PERSONA_MOCKS[personaId];
  return {
    provider: synthesizeProvider(personaId, mock),
    reputation: synthesizeReputation(mock),
    services: synthesizeServices(mock),
    reviews: synthesizeReviews(mock),
    responseTimeHours: RESPONSE_TIME_HOURS,
    intents: ['book', 'project', 'message'],
    subscriptionActive: true,
  };
};
